import { computed, inject, Injectable, signal } from '@angular/core';
import { SessionStorageService } from './session-storage.service';
import { ViewportService } from './viewport.service';
import { parseServerTimestamp } from '../util/date.util';

const IN_FLIGHT_STORAGE_KEY = 'collectiviq.messagesInFlight';
/** Longest a run may be assumed in flight before callers treat it as dead. */
export const IN_FLIGHT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

type SidebarState = {
    isOpen: boolean;
};

type InFlightInfo = {
    promptCreateTime: string; // ISO timestamp of when the prompt was created
};

type MessagesInFlight = Record<number, InFlightInfo>;

export type ApiKeyForceRotatedBanner = {
    actorEmail: string;
    keyName: string;
    displayPrefix: string;
    oldApiKeyId: number;
};

@Injectable({
    providedIn: 'root',
})
export class AppStateService {
    private readonly storage = inject(SessionStorageService);
    private readonly viewport = inject(ViewportService);

    // Default open on desktop, closed on mobile. Initial value comes from a
    // synchronous viewport read so the sidebar lands in its final state on
    // first paint (no flash-of-open followed by an effect-driven snap to
    // closed). Once the user toggles, that's the source of truth - we no
    // longer watch viewport changes, because mobile browsers fire constant
    // resize events (URL bar collapse, soft keyboard, orientation, pinch
    // zoom), and any auto-snap was the symptom behind "tap hamburger →
    // sidebar pops open for a second → closes itself". Manual toggle only.
    private readonly sidebarStateSignal = signal<SidebarState>({ isOpen: !this.viewport.isMobileDimension() });
    // Don't restore - stale in-flight state causes stuck loading indicators
    private readonly inFlightSignal = signal<MessagesInFlight>({});
    // In-app banner shown when an admin force-rotates the user's
    // api key. Cleared by the dismiss CTA on the banner template.
    private readonly apiKeyForceRotatedBannerSignal = signal<ApiKeyForceRotatedBanner | null>(null);

    /** True when the Approvals workspace panel is active - suppresses toasts. */
    private readonly approvalsViewActiveSignal = signal(false);

    /** Mobile-only: whether the in-place approval toast stack is expanded. Two
     *  triggers set it: the blue approval bar above the DDR-room composer, and
     *  the floating pill on workspace pages. Desktop shows the stack
     *  unconditionally and ignores this. */
    private readonly mobileApprovalsOpenSignal = signal(false);

    readonly navState = this.sidebarStateSignal.asReadonly();
    readonly messagesInFlight = this.inFlightSignal.asReadonly();
    readonly hasAnyInFlight = computed(() => Object.keys(this.inFlightSignal()).length > 0);
    readonly apiKeyForceRotatedBanner = this.apiKeyForceRotatedBannerSignal.asReadonly();
    readonly approvalsViewActive = this.approvalsViewActiveSignal.asReadonly();
    readonly mobileApprovalsOpen = this.mobileApprovalsOpenSignal.asReadonly();

    toggleMobileApprovals() {
        this.mobileApprovalsOpenSignal.update(open => !open);
    }

    setMobileApprovalsOpen(open: boolean) {
        this.mobileApprovalsOpenSignal.set(open);
    }

    toggleSidebarOpenChange() {
        this.sidebarStateSignal.update(prev => ({ isOpen: !prev.isOpen }));
    }

    setSidebarOpen(isOpen: boolean) {
        this.sidebarStateSignal.set({ isOpen });
    }

    setThreadInFlight(threadId: number, inFlight: boolean, promptCreateTime?: string) {
        if (!threadId) {
            return;
        }

        this.inFlightSignal.update(prev => {
            const next = { ...prev };

            if (!inFlight) {
                delete next[threadId];
            } else {
                next[threadId] = {
                    promptCreateTime: promptCreateTime ?? new Date().toISOString(),
                };
            }

            return next;
        });

        this.persistInFlightState();
    }

    isThreadInFlight(threadId: number | null | undefined): boolean {
        if (!threadId) {
            return false;
        }

        const inFlightInfo = this.inFlightSignal()[threadId];
        if (!inFlightInfo) {
            return false;
        }

        // Auto-unlock if the prompt was created more than 5 minutes ago
        const promptTime = parseServerTimestamp(inFlightInfo.promptCreateTime) ?? NaN;
        const now = Date.now();
        if (now - promptTime > IN_FLIGHT_TIMEOUT_MS) {
            // Clear the stale in-flight state
            this.setThreadInFlight(threadId, false);
            return false;
        }

        return true;
    }

    clearAllInFlight() {
        this.inFlightSignal.set({});
        this.persistInFlightState();
    }

    setApiKeyForceRotatedBanner(banner: ApiKeyForceRotatedBanner) {
        this.apiKeyForceRotatedBannerSignal.set(banner);
    }

    clearApiKeyForceRotatedBanner() {
        this.apiKeyForceRotatedBannerSignal.set(null);
    }

    setApprovalsViewActive(active: boolean) {
        this.approvalsViewActiveSignal.set(active);
    }

    private restoreInFlightState(): MessagesInFlight {
        return this.storage.read<MessagesInFlight>(IN_FLIGHT_STORAGE_KEY, {});
    }

    private persistInFlightState() {
        const current = this.inFlightSignal();

        if (Object.keys(current).length === 0) {
            this.storage.remove(IN_FLIGHT_STORAGE_KEY);

            return;
        }

        this.storage.write(IN_FLIGHT_STORAGE_KEY, current);
    }
}
